import { ITrainingComponent, ITrainingComponentPayload, TrainingComponentType } from 'interfaces/training-component'
import { LibraryChild, LibraryDetail, LibraryItem, LibraryItemType, LibraryMeta } from 'interfaces/practice-component'

// Card-meta icon names (SvgIcon), matching the BuilderLibrary card design:
// duration + notes on the first row, source plan + child count on the second.
const ICON_CLOCK = 'ic_outline-alarm'
const ICON_COUNT = 'ic_outline-account-tree'
const ICON_NOTES = 'note-rallypd'
const ICON_PLAN = 'training-plan'

// Direct-child noun per component level, used for "N days" / "N drills" summaries.
const CHILD_NOUN: Record<TrainingComponentType, string> = {
  week: 'day',
  day: 'block',
  block: 'station',
  station: 'drill'
}

function pluralize(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`
}

function capitalize(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1)
}

/** Direct-child count of a payload node (drills for a station, else children). */
function childCountOf(node: ITrainingComponentPayload): number {
  return node.type === 'station' ? node.drills?.length ?? 0 : node.children?.length ?? 0
}

function summarizeNode(node: ITrainingComponentPayload): string {
  return pluralize(childCountOf(node), CHILD_NOUN[node.type])
}

/**
 * Build the right-panel preview detail from a component's payload.
 * Only available on the detail DTO (list results exclude payload).
 */
function buildDetailFromPayload(payload: ITrainingComponentPayload): LibraryDetail {
  const root = payload.root as { objective?: string; note?: string; estimatedDuration?: number }
  // Objective is always set (empty when the level's model has none — day/block)
  // so every training preview shows the row with an em-dash, matching Notes.
  const detail: LibraryDetail = {
    childLabel: capitalize(CHILD_NOUN[payload.type]) + 's',
    objective: root.objective ?? '',
    notes: root.note
  }

  if (payload.type === 'station') {
    // The prescription is part of what a coach saved the station for, so the
    // preview shows it the way the inspector does.
    detail.trainingRx = payload.trainingRx
    detail.children = (payload.drills ?? []).map<LibraryChild>(drill => ({
      name: drill.name ?? 'Drill',
      meta: typeof drill.duration === 'number' ? `${drill.duration} min` : ''
    }))
  } else {
    detail.children = (payload.children ?? []).map<LibraryChild>(child => ({
      name: (child.root?.name as string) ?? '',
      meta: summarizeNode(child)
    }))
  }

  return detail
}

/**
 * Map a TrainingComponent DTO to a BuilderLibrary card view model. Reuses the
 * generic LibraryItem/LibraryDetail/LibraryChild view-model types from
 * `interfaces/practice-component` (DRY) — only the component-type union
 * differs (`week`/`day`/`block`/`station`, no `rotation`).
 *
 * `dto.type` is bridged through `unknown` before assigning to `LibraryItem.type`:
 * `TrainingComponentType` and the Practice-derived `LibraryItemType` are two
 * distinct string-literal unions with partial overlap (`block`/`station`) but
 * neither is a subset of the other (`week`/`day` vs `period`/`lane`/`rotation`),
 * so TS rejects a direct `as` cast. The Practice union is intentionally left
 * unwidened (per spec); `type` is only read downstream as an opaque
 * display/routing tag, never exhaustively switched against the Practice union.
 */
export function mapTrainingComponentToLibraryItem(dto: ITrainingComponent): LibraryItem {
  const type = dto.type as unknown as LibraryItemType
  // Order matters — the card lays these out in a Wrap, so it reads
  // "duration · notes / plan · N children". Absent fields drop their row
  // entirely rather than rendering a placeholder; only Block carries a duration
  // in training, so week/day/station cards start at the notes row.
  // Tags are NOT repeated here; they already render as chips above the meta rows.
  const meta: LibraryMeta[] = []
  if (dto.durationLabel) meta.push({ icon: ICON_CLOCK, text: dto.durationLabel })
  if (dto.notes) meta.push({ icon: ICON_NOTES, text: dto.notes })
  if (dto.planName) meta.push({ icon: ICON_PLAN, text: dto.planName })
  // metaSummary is the BE-pluralized "N days" string — using it keeps the
  // singular/plural rule in one place instead of re-deriving it from childType.
  if (dto.metaSummary) meta.push({ icon: ICON_COUNT, text: dto.metaSummary })

  const item: LibraryItem = {
    id: dto.id,
    key: `${dto.type}:${dto.id}`,
    title: dto.name,
    type,
    meta,
    tags: dto.tags
  }

  if (dto.payload) {
    item.detail = buildDetailFromPayload(dto.payload)
    if (dto.durationLabel) {
      item.detail.duration = dto.durationLabel
    }
  }

  return item
}
